import { useEffect, useState } from 'react';
import { Table, Spinner, Badge, Pagination } from 'react-bootstrap';
import { acquireTokenForApi } from '@/authConfig';
import "@/pages/MyActivityLog/MyActivityLog.scss";

interface ActivityLogEvent {
  guid: string;
  message: string;
  messagesTitle: string;
  createDt: string;
  status: string;
}

export default function MyActivityLog() {
  const [events, setEvents] = useState<ActivityLogEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const pageSize = 20;

  useEffect(() => { loadActivityLog(); }, []);

  // Auto-refresh when user returns to page
  useEffect(() => {
    const handleFocus = () => {
      loadActivityLog();
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  async function loadActivityLog() {
    setLoading(true);
    try {
      const token = await acquireTokenForApi();

      const resIds = await fetch(`${import.meta.env.VITE_API_URL}/api/activitylog/ids`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const ids = await resIds.json();
      const allEvents: ActivityLogEvent[] = [];

      for (const id of ids.slice(0, 100)) {
        const res = await fetch(`${import.meta.env.VITE_API_URL}/api/activitylog/${id.guid}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = await res.json();
        allEvents.push(data[0]);
      }

      setEvents(allEvents);
    } finally {
      setLoading(false);
    }
  }

  const start = (page - 1) * pageSize;
  const current = events.slice(start, start + pageSize);
  const totalPages = Math.ceil(events.length / pageSize);

  return (
    <div className="activitylog-page">
      <h3 className="page-title mb-2">My Activity Log</h3>
      <p className="text-muted page-description">
        A record of your portal activity including group actions, resource updates, and user-triggered operations.
      </p>

      {loading ? (
        <div className="loading-container">
          <Spinner animation="border" role="status" />
          <div className="mt-3 text-muted">Loading activity...</div>
        </div>
      ) : (
        <>
          <div className="activitylog-card">
            <Table hover responsive className="activitylog-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Message</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {current.map(evt => (
                  <tr key={evt.guid}>
                    <td>{new Date(evt.createDt).toLocaleString()}</td>
                    <td dangerouslySetInnerHTML={{ __html: evt.message }} />
                    <td>
                      <Badge
                        bg={(evt.status === 'Success' || evt.status === 'Completed')
                          ? 'success'
                          : evt.status === 'Failed'
                          ? 'danger'
                          : evt.status === 'New'
                          ? 'primary'
                          : 'secondary'}
                        className="status-badge"
                      >
                        {evt.status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {totalPages > 1 && (
            <Pagination className="justify-content-end mt-3">
              <Pagination.First onClick={() => setPage(1)} disabled={page === 1} />
              <Pagination.Prev onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} />
              <Pagination.Item active>{page}</Pagination.Item>
              <Pagination.Next onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} />
              <Pagination.Last onClick={() => setPage(totalPages)} disabled={page === totalPages} />
            </Pagination>
          )}
        </>
      )}
    </div>
  );
}